import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiDownload, FiLoader, FiPlus, FiTrash2, FiUploadCloud, FiX } from "react-icons/fi";
import { HandleAxiosPromise } from "@shared_components/axios/HandleAxiosPromise";
import { useMessage } from "@shared_components/providers/MessageProvider";
import { GEARSTUDIO_GENERATE_USER_IMAGE_URL, GEARSTUDIO_USER_IMAGES_URL, GearstudioUserImage } from "@src/types";
import { getImageSrc } from "./editdesignview.utils";

type ImageManagerModalProps = {
    open: boolean;
    user_uuid: string;
    onClose: () => void;
    onAddImageToPanel: (asset: GearstudioUserImage) => void;
};

const PAGE_SIZE = 12;

const getThumbnailSrc = (image: GearstudioUserImage) => {
    return (
             image.images_data.thumbnail_medium
        || image.images_data.thumbnail_large
        || image.images_data.webp
        || image.images_data.src
    );
};

const parseImageResults = (payload: unknown): GearstudioUserImage[] => {
    if (!payload || typeof payload !== "object") {
        return [];
    }

    const normalizedPayload = payload as { results?: unknown };
    if (!Array.isArray(normalizedPayload.results)) {
        return [];
    }
    return normalizedPayload.results as GearstudioUserImage[];
};

export const ImageManagerModal = ({ open, user_uuid, onClose, onAddImageToPanel }: ImageManagerModalProps) => {
    const { setTextData } = useMessage();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [images, setImages] = useState<GearstudioUserImage[]>([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [generatingAiImage, setGeneratingAiImage] = useState(false);
    const [deletingImageId, setDeletingImageId] = useState<number | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [previewImage, setPreviewImage] = useState<GearstudioUserImage | null>(null);
    const [aiPrompt, setAiPrompt] = useState("");

    const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount]);
    const showStart = images.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const showEnd = Math.min(totalCount, (currentPage - 1) * PAGE_SIZE + images.length);

    const fetchImages = useCallback(async (page: number) => {
        if (!user_uuid) {
            return;
        }

        setLoadingImages(true);
        try {
            const response = await HandleAxiosPromise({
                url: GEARSTUDIO_USER_IMAGES_URL,
                method: "get",
                data: {
                    offset: (page - 1) * PAGE_SIZE,
                    limit: PAGE_SIZE,
                },
                additional_headers: { "User-Id": user_uuid },
            });
            const payload = response?.data || {};
            const nextImages = parseImageResults(payload);
            const nextCount = typeof payload?.count === "number" ? payload.count : nextImages.length;
            const nextTotalPages = Math.max(1, Math.ceil(nextCount / PAGE_SIZE));

            setImages(nextImages);
            setTotalCount(nextCount);
            if (page > nextTotalPages) {
                setCurrentPage(nextTotalPages);
            }
        } catch (error) {
            const message = typeof error === "string" ? error : "Failed to load images.";
            setTextData({ text: message, variant: "danger" });
            setImages([]);
            setTotalCount(0);
        } finally {
            setLoadingImages(false);
        }
    }, [setTextData, user_uuid]);

    useEffect(() => {
        if (!open) {
            return;
        }
        setCurrentPage(1);
        setPreviewImage(null);
        setAiPrompt("");
    }, [open]);

    useEffect(() => {
        if (!open || !user_uuid) {
            return;
        }
        fetchImages(currentPage);
    }, [currentPage, fetchImages, open, user_uuid]);

    const uploadFiles = async (files: File[]) => {
        if (!user_uuid || files.length === 0) {
            return;
        }

        const validFiles = files.filter((file) => file.type.startsWith("image/"));
        if (validFiles.length === 0) {
            setTextData({ text: "No valid image files selected.", variant: "warning" });
            return;
        }

        const formData = new FormData();
        validFiles.forEach((file) => {
            formData.append("image_data", file);
        });

        setUploadingImages(true);
        try {
            const response = await HandleAxiosPromise({
                url: GEARSTUDIO_USER_IMAGES_URL,
                method: "post",
                data: formData,
                contentType: "multipart/form-data",
                additional_headers: { "User-Id": user_uuid },
            });
            const createdImages = parseImageResults(response?.data || {});
            const uploadCount = createdImages.length || validFiles.length;
            setTextData({
                text: uploadCount === 1 ? "1 image uploaded." : `${uploadCount} images uploaded.`,
                variant: "success",
            });

            if (currentPage !== 1) {
                setCurrentPage(1);
            } else {
                fetchImages(1);
            }
        } catch (error) {
            const message = typeof error === "string" ? error : "Failed to upload images.";
            setTextData({ text: message, variant: "danger" });
        } finally {
            setUploadingImages(false);
        }
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        uploadFiles(files);
        event.currentTarget.value = "";
    };

    const handleDeleteImage = async (image: GearstudioUserImage) => {
        if (!user_uuid) {
            return;
        }
        const confirmed = window.confirm(`Delete "${image.name}"? This cannot be undone.`);
        if (!confirmed) {
            return;
        }

        setDeletingImageId(image.id);
        try {
            await HandleAxiosPromise({
                url: `${GEARSTUDIO_USER_IMAGES_URL}${image.id}/`,
                method: "delete",
                additional_headers: { "User-Id": user_uuid },
            });
            setTextData({ text: "Image deleted.", variant: "success" });

            const nextCount = Math.max(0, totalCount - 1);
            const nextTotalPages = Math.max(1, Math.ceil(nextCount / PAGE_SIZE));
            const nextPage = Math.min(currentPage, nextTotalPages);
            if (nextPage !== currentPage) {
                setCurrentPage(nextPage);
            } else {
                fetchImages(nextPage);
            }
        } catch (error) {
            const message = typeof error === "string" ? error : "Failed to delete image.";
            setTextData({ text: message, variant: "danger" });
        } finally {
            setDeletingImageId(null);
        }
    };

    const handleDownloadImage = (image: GearstudioUserImage) => {
        const link = document.createElement("a");
        link.href = getImageSrc(image);
        link.download = image.name || `gearstudio-image-${image.id}`;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleGenerateAiImage = async () => {
        if (!user_uuid) {
            return;
        }

        const trimmedPrompt = aiPrompt.trim();
        if (!trimmedPrompt) {
            setTextData({ text: "Please enter a prompt for AI image generation.", variant: "warning" });
            return;
        }

        setGeneratingAiImage(true);
        try {
            const response = await HandleAxiosPromise({
                url: GEARSTUDIO_GENERATE_USER_IMAGE_URL,
                method: "post",
                data: {
                    user_prompt: trimmedPrompt,
                },
                additional_headers: { "User-Id": user_uuid },
            });

            const createdImages = parseImageResults(response?.data || {});
            if (createdImages.length === 0) {
                setTextData({ text: "No AI image was generated. Try a different prompt.", variant: "warning" });
                return;
            }

            setTextData({ text: "AI image generated.", variant: "success" });
            setAiPrompt("");
            if (currentPage !== 1) {
                setCurrentPage(1);
            } else {
                fetchImages(1);
            }
        } catch (error) {
            const message = typeof error === "string" ? error : "Failed to generate AI image.";
            setTextData({ text: message, variant: "danger" });
        } finally {
            setGeneratingAiImage(false);
        }
    };

    if (!open) {
        return null;
    }

    return (
        <>
            <div className="fixed inset-0 z-50 bg-black/45 p-4 sm:p-8">
                <div className="mx-auto flex  w-full max-w-3xl flex-col overflow-hidden rounded-[var(--gs-radius-card)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface)] shadow-xl">
                    <div className="flex items-center justify-between border-b border-[color:var(--gs-border-subtle)] px-4 py-3">
                        <h3 className="gs-primary text-base font-semibold">Image Manager</h3>
                        <button
                            type="button"
                            onClick={onClose}
                            className="gs-outline-btn px-3 py-1 text-xs font-semibold"
                        >
                            Close
                        </button>
                    </div>

                    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
                        <div
                            className={`rounded-[var(--gs-radius-input)] border border-dashed p-4 transition-colors ${dragActive ? "border-[color:var(--gs-accent-primary)] bg-[color:rgba(201,107,59,0.1)]" : "border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface-alt)]"}`}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setDragActive(true);
                            }}
                            onDragLeave={(event) => {
                                event.preventDefault();
                                setDragActive(false);
                            }}
                            onDrop={(event) => {
                                event.preventDefault();
                                setDragActive(false);
                                uploadFiles(Array.from(event.dataTransfer.files || []));
                            }}
                        >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <p className="gs-primary text-sm font-semibold">Upload Images</p>
                                    <p className="gs-muted text-xs">
                                        Drop images here or upload from your computer.
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        className="gs-btn-primary px-3 py-2 text-xs font-semibold"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingImages || generatingAiImage}
                                    >
                                        {uploadingImages ? (
                                            <>
                                                <FiLoader className="mr-1 inline h-3.5 w-3.5 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <FiUploadCloud className="mr-1 inline h-3.5 w-3.5" />
                                                Upload From Computer
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={handleFileInputChange}
                            />
                        </div>

                        <div className="rounded-[var(--gs-radius-input)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface-alt)] p-4">
                            <div className="flex flex-col gap-3">
                                <div>
                                    <p className="gs-primary text-sm font-semibold">Generate AI Image</p>
                                    <p className="gs-muted text-xs">
                                        Generate a new image from a prompt.
                                    </p>
                                </div>
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <input
                                        type="text"
                                        value={aiPrompt}
                                        onChange={(event) => setAiPrompt(event.target.value)}
                                        placeholder="Describe the image to generate"
                                        className="gs-input flex-1 px-3 py-2 text-xs"
                                        disabled={uploadingImages || generatingAiImage}
                                    />
                                    <button
                                        type="button"
                                        className="gs-outline-btn px-3 py-2 text-xs font-semibold"
                                        onClick={handleGenerateAiImage}
                                        disabled={uploadingImages || generatingAiImage || !aiPrompt.trim()}
                                    >
                                        {generatingAiImage ? (
                                            <>
                                                <FiLoader className="mr-1 inline h-3.5 w-3.5 animate-spin" />
                                                Generating...
                                            </>
                                        ) : (
                                            "Generate AI Image"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <p className="gs-muted text-xs">
                                Showing {showStart}-{showEnd} of {totalCount}
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    className="gs-outline-btn px-3 py-1 text-xs font-semibold"
                                    disabled={currentPage <= 1 || loadingImages}
                                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                >
                                    Previous
                                </button>
                                <span className="gs-muted text-xs">
                                    Page {currentPage} / {totalPages}
                                </span>
                                <button
                                    type="button"
                                    className="gs-outline-btn px-3 py-1 text-xs font-semibold"
                                    disabled={currentPage >= totalPages || loadingImages}
                                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                                >
                                    Next
                                </button>
                            </div>
                        </div>

                        {loadingImages ? (
                            <div className="gs-surface-alt flex min-h-[260px] items-center justify-center rounded-[var(--gs-radius-input)]">
                                <FiLoader className="h-5 w-5 animate-spin text-[var(--gs-accent-primary)]" />
                            </div>
                        ) : images.length === 0 ? (
                            <div className="gs-surface-alt rounded-[var(--gs-radius-input)] p-4 text-sm text-[var(--gs-text-secondary)]">
                                No images yet. Upload one to get started.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                {images.map((image) => (
                                    <div
                                        key={image.id}
                                        className="rounded-[var(--gs-radius-input)] border border-[color:var(--gs-border-subtle)] p-2"
                                    >
                                        <button
                                            type="button"
                                            className="block w-full rounded"
                                            onClick={() => setPreviewImage(image)}
                                        >
                                            <img
                                                src={getThumbnailSrc(image)}
                                                alt={image.name}
                                                className="h-36 w-full rounded object-contain bg-[color:var(--gs-bg-surface-alt)]"
                                            />
                                        </button>
                                        <p className="mt-2 truncate text-xs font-semibold text-[var(--gs-text-primary)]">
                                            {image.name}
                                        </p>
                                        <div className="mt-2 grid grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => onAddImageToPanel(image)}
                                                className="gs-btn-primary px-2 py-1 text-xs"
                                            >
                                                <FiPlus className="mr-1 inline h-3.5 w-3.5" />
                                                Add
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDownloadImage(image)}
                                                className="gs-outline-btn px-2 py-1 text-xs"
                                            >
                                                <FiDownload className="mr-1 inline h-3.5 w-3.5" />
                                                Download
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteImage(image)}
                                                disabled={deletingImageId === image.id}
                                                className="gs-outline-btn col-span-2 px-2 py-1 text-xs"
                                            >
                                                {deletingImageId === image.id ? (
                                                    <>
                                                        <FiLoader className="mr-1 inline h-3.5 w-3.5 animate-spin" />
                                                        Deleting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiTrash2 className="mr-1 inline h-3.5 w-3.5" />
                                                        Delete
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {previewImage ? (
                <div className="fixed inset-0 z-[60] bg-black/70 p-4 sm:p-8" onClick={() => setPreviewImage(null)}>
                    <div
                        className="mx-auto flex h-full w-full max-w-5xl flex-col rounded-[var(--gs-radius-card)] bg-[color:var(--gs-bg-surface)] p-4"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <p className="gs-primary truncate text-sm font-semibold">{previewImage.name}</p>
                            <button
                                type="button"
                                className="gs-outline-btn px-2 py-1 text-xs"
                                onClick={() => setPreviewImage(null)}
                            >
                                <FiX className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex min-h-0 flex-1 items-center justify-center">
                            <img
                                src={getImageSrc(previewImage)}
                                alt={previewImage.name}
                                className="max-h-full max-w-full rounded object-contain"
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};
